import dayjs from 'dayjs'
require('dayjs/locale/it')
import customParseFormat from 'dayjs/plugin/customParseFormat'
import {
  BRAND,
  HEADER_BREAKPOINT,
  HEADER_HEIGHT,
  HEADER_HEIGHT_MOBILE,
  TOOLBAR_HEIGHT,
} from 'utils/settings'

export const dateFormat = (valore: string) => dayjs(valore, 'YYYY-MM-DD').format('DD/MM/YYYY')
export const magazineDateFormat = (valore: string) =>
  dayjs(valore, 'YYYY-MM-DD').locale('it').format('DD MMMM YYYY')
export const datePickerFormat = (valore: string | Date) => dayjs(valore).format('YYYY-MM-DD')
export const monthYearFormat = (valore: string, locale?: string | undefined) => {
  return dayjs(valore, 'YYYY-MM-DD')
    .toDate()
    .toLocaleDateString(locale, { year: 'numeric', month: 'long' })
}
export const localeDateFormat = (valore: string, locale?: string | undefined) => {
  if (locale) {
    dayjs.locale(locale)
  }
  return dayjs(valore, 'YYYY-MM-DD')
    .toDate()
    .toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' })
}

export const isDateValid = (valore: string, format: string) => {
  dayjs.extend(customParseFormat)
  return dayjs(valore, format, true).isValid()
}

export const getFormFieldArgs = (
  name: string,
  values: any,
  errors: any,
  touched: any,
  space?: 'md' | 'sm' | 'none' // | 'lg' | 'xl' | undefined
): {
  space?: 'md' | 'sm' | 'none' // | 'lg' | 'xl' | undefined
} => ({
  space: space || 'md',
})

export const getFormComponentArgs = (
  name: string,
  errors: any,
  touched: any
): {
  status?: '' | 'success' | 'error' | undefined
  errorMessage?: string
} => ({
  status: touched[name] && errors[name] ? 'error' : '',
  errorMessage: errors[name],
})

export const isEmpty = (obj: Record<string, unknown>) => {
  return Object.keys(obj).length === 0 && obj.constructor === Object
}

declare global {
  interface String {
    toGTMFormat(): string
    toHotjarFormat(v: string, isMobile?: boolean): string
    capitalize(): string
  }
}

String.prototype.toGTMFormat = function () {
  return this.replace(/<\/?[^>]+(>|$)/g, '')
    .replace(/\s+/g, '_')
    .toLowerCase()
}

String.prototype.toHotjarFormat = function (value: string, isMobile: boolean = false) {
  return `${value.replace('{0}', this.replace(/\.|\/|\s+/g, '_').toLowerCase())}${
    isMobile ? '_mobile' : ''
  }`
}

String.prototype.capitalize = function () {
  return this.charAt(0).toUpperCase() + this.slice(1)
}

export function escape(s: String) {
  return s
    .replace(/&amp;/g, '&')
    .replace(/&#39;/g, "'")
    .replace(/&quot;/g, '"')
}

export const toAltTitle = (term: string) =>
  term ? `${term} | ${BRAND}`.replace(/  +/g, ' ') : BRAND

export const getYearOptions = (yearFrom: number, yearNumber: number) =>
  [...Array.from({ length: yearNumber }).keys()].map((i) => ({
    label: (i + yearFrom).toString(),
    value: (i + yearFrom).toString(),
  }))

export const closest = (arr: number[], goal: number) => {
  return arr.reduce((prev, curr) => (Math.abs(curr - goal) < Math.abs(prev - goal) ? curr : prev))
}

export const numberNotation = (valore: any, numeroDecimali?: number) => {
  const valoreSafe = Number.parseFloat(valore) || 0
  const [intPart, decimals] = valoreSafe.toFixed(numeroDecimali).toString().split('.')
  return intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.') + (numeroDecimali > 0 ? `,${decimals}` : '')
}

// Lo distinguo da numberNotation, non si sa mai che i prezzi possano avere un formato diverso successivamente (es includere valuta)
export const priceNotation = (valore: any) => `${numberNotation(valore, 0)} €`

export const scrollTo = (ref: any, toolbar: boolean, offset?: number, offsetMobile?: number) => {
  const headerHeight = window.innerWidth < HEADER_BREAKPOINT ? HEADER_HEIGHT_MOBILE : HEADER_HEIGHT
  const y =
    ref.offsetTop -
    (window.innerWidth < HEADER_BREAKPOINT ? offsetMobile || offset || 0 : offset || 0) -
    headerHeight

  window.scrollTo({
    top: y + (y > window.scrollY ? headerHeight : 0) - (toolbar ? TOOLBAR_HEIGHT : 0), // se vado giù l'header sparisce quindi scendo un po' di più
    behavior: 'smooth',
  })
}

export const getFormFieldStatus = (formikProps: any, propName: string) =>
  formikProps.touched[propName] ? (formikProps.errors[propName] ? 'error' : 'success') : undefined

// Id GraphQl
export const fromGlobalID = (globalID: string): string => atob(globalID).split(':')[1]
export const toGlobalID = (localID: number, nodeType: string) => btoa(`${nodeType}:${localID}`)

export const getFileNameFromUrl = (url: string) => {
  let name = ''
  if (url) {
    name = url.split('/')[url.split('/').length - 1] || ''
    return name
  } else {
    return ''
  }
}

const emailRegex =
  /^[-!#$%&'*+\/0-9=?A-Z^_a-z`{|}~](\.?[-!#$%&'*+\/0-9=?A-Z^_a-z`{|}~])*@[a-zA-Z0-9](-*\.?[a-zA-Z0-9])*\.[a-zA-Z](-?[a-zA-Z0-9])+$/

// EmailValidator (https://github.com/manishsaraan/email-validator/blob/master/index.js)
export const validateEmail = (email: string | undefined) => {
  if (!email) return false

  var emailParts = email.split('@')

  if (emailParts.length !== 2) return false

  var account = emailParts[0]
  var address = emailParts[1]

  if (account.length > 64) return false
  else if (address.length > 255) return false

  var domainParts = address.split('.')

  if (
    domainParts.some(function (part) {
      return part.length > 63
    })
  )
    return false

  return emailRegex.test(email)
}

export const isOutsideClick = (e, ref) => {
  var rect = ref.current?.getBoundingClientRect()
  return !(
    rect.top <= e.clientY &&
    e.clientY <= rect.top + rect.height &&
    rect.left <= e.clientX &&
    e.clientX <= rect.left + rect.width
  )
}
